import { Button } from "@/components/ui/button";
import { LogOut, Monitor, Smartphone, Upload, Tag, CreditCard, User, X, History, ChevronUp, ChevronDown, Palette, Key, Tv, Building2, Rocket, Zap, Settings, Handshake, Power, Clock, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/lib/supabase";

// Estilos CSS para scrollbar customizada - UX/UI aprimorado
const scrollbarStyles = `
  .sidebar-scroll {
    scrollbar-gutter: stable;
  }
  
  .sidebar-scroll::-webkit-scrollbar {
    width: 4px;
  }
  
  .sidebar-scroll::-webkit-scrollbar-track {
    background: transparent;
    margin: 8px 0;
  }
  
  .sidebar-scroll::-webkit-scrollbar-thumb {
    background: rgba(156, 163, 175, 0.4);
    border-radius: 100px;
    transition: background 0.2s ease;
  }
  
  .sidebar-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(107, 114, 128, 0.6);
  }
  
  /* Dark mode scrollbar */
  .dark .sidebar-scroll::-webkit-scrollbar-thumb {
    background: rgba(75, 85, 99, 0.5);
  }
  
  .dark .sidebar-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(107, 114, 128, 0.7);
  }
  
  /* Firefox scrollbar */
  .sidebar-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(156, 163, 175, 0.4) transparent;
  }
  
  .dark .sidebar-scroll {
    scrollbar-color: rgba(75, 85, 99, 0.5) transparent;
  }
`;

type Screen = {
  id: string;
  name: string;
};

type DashboardSidebarProps = {
  onNewScreen: () => void;
  onNewMedia: () => void;
  onNewDevice: () => void;
  onNewPromo: () => void;
  onNewCreatives: () => void;
  onWatchAds: () => void;
  onOpenProfile: () => void;
  onChangePassword: () => void;
  onClose?: () => void;
  onOpenMyPlan: () => void;
  onOpenPaymentHistory: () => void;
  onOpenVenuePartner: () => void;
  onOpenNoActiveDevices: () => void;
  onOpenPlayerSchedule: () => void;
  screens: Screen[];
  hasActiveDevices: boolean;
};

const DashboardSidebar = ({
  onNewScreen, onNewMedia, onNewDevice, onNewPromo, onNewCreatives,
  onWatchAds, onOpenProfile, onChangePassword, onClose,
  onOpenMyPlan, onOpenPaymentHistory, onOpenVenuePartner, onOpenNoActiveDevices, onOpenPlayerSchedule,
  screens, hasActiveDevices
}: DashboardSidebarProps) => {
  const navigate = useNavigate();
  const { signOut, user, profile } = useAuth();
  const { settings } = useSystemSettings();
  const [isActionsCollapsed, setIsActionsCollapsed] = useState(false);

  // Estado para colapso da seção Impulsionar
  const [isServicesCollapsed, setIsServicesCollapsed] = useState(true);
  // Estado para colapso da seção Configurações
  const [isConfigCollapsed, setIsConfigCollapsed] = useState(true);
  // Estado para colapso da seção Seja um parceiro
  const [isPartnerCollapsed, setIsPartnerCollapsed] = useState(true);

  // Estado para colapso da seção de Desligamento Automático
  const [isShutdownCollapsed, setIsShutdownCollapsed] = useState(true);

  // Estado para toggle manual de ligar/desligar player
  const [isPlayerManuallyOn, setIsPlayerManuallyOn] = useState(true);
  const [isLoadingManualToggle, setIsLoadingManualToggle] = useState(true);

  // Carregar estado manual do banco de dados
  useEffect(() => {
    if (!user?.id) return;

    const loadManualState = async () => {
      try {
        console.log('[SIDEBAR] Carregando estado manual para userId:', user.id);

        const { data, error } = await supabase
          .from('user_shutdown_schedules')
          .select('is_manually_on')
          .eq('user_id', user.id)
          .single();

        console.log('[SIDEBAR] Resposta do banco:', { data, error });

        if (error) {
          console.log('[SIDEBAR] Erro ao carregar, usando padrão LIGADO:', error.message);
          setIsPlayerManuallyOn(true);
        } else if (data && data.is_manually_on !== null && data.is_manually_on !== undefined) {
          console.log(`[SIDEBAR] Valor carregado do banco: is_manually_on = ${data.is_manually_on}`);
          setIsPlayerManuallyOn(data.is_manually_on);
        } else {
          // Se não existir, criar registro com valor padrão LIGADO
          console.log('[SIDEBAR] Sem registro, iniciando com toggle LIGADO');
          setIsPlayerManuallyOn(true);
        }
      } catch (err) {
        console.error('[SIDEBAR] Erro inesperado ao carregar estado manual:', err);
        setIsPlayerManuallyOn(true);
      } finally {
        setIsLoadingManualToggle(false);
      }
    };

    loadManualState();
  }, [user?.id]);

  // Função para verificar se tem plano ativo
  const hasActivePlan = () => {
    if (!profile) return false;

    // Se tem status ativo
    if (profile.subscription_status === 'active') return true;

    // Verificar Trial (7 dias)
    const createdAt = profile.created_at;
    if (createdAt && (!profile.subscription_status || profile.subscription_status === 'trialing')) {
      const accountAge = Date.now() - new Date(createdAt).getTime();
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
      return accountAge < sevenDaysInMs;
    }

    return false;
  };

  // Sincronizar em Realtime via Supabase
  useEffect(() => {
    if (!user?.id) return;

    console.log('📡 [SIDEBAR] Inscrevendo em updates realtime para toggle manual');

    // Canal único para evitar multiplicidade
    const channelName = `sidebar_shutdown_sync_${user.id}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_shutdown_schedules',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('🔄 [SIDEBAR] Update recebido via Realtime:', payload);
          const newRecord = payload.new as { is_manually_on: boolean | null };

          if (newRecord.is_manually_on !== undefined && newRecord.is_manually_on !== null) {
            console.log(`🔄 [SIDEBAR] Atualizando toggle visual para: ${newRecord.is_manually_on}`);
            setIsPlayerManuallyOn(newRecord.is_manually_on);
          }
        }
      )
      .subscribe();

    return () => {
      console.log('🔌 [SIDEBAR] Desinscrevendo realtime');
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // Função para alternar o toggle manual e salvar no banco
  const handleManualToggle = async (checked: boolean) => {
    console.log(`🔌 [SIDEBAR] Toggle manual: ${checked ? 'LIGANDO' : 'DESLIGANDO'} player`);
    console.log(`🔌 [SIDEBAR] Valor a ser salvo: is_manually_on = ${checked}`);

    // Atualiza estado local imediatamente para UX responsiva
    setIsPlayerManuallyOn(checked);

    if (!user?.id) {
      console.error('[SIDEBAR] ❌ Sem user.id, não salvando estado manual');
      return;
    }

    try {
      console.log(`[SIDEBAR] Salvando no banco para userId: ${user.id}`);

      // Usar upsert com onConflict para garantir que funcione
      const { data, error } = await supabase
        .from('user_shutdown_schedules')
        .upsert(
          {
            user_id: user.id,
            is_manually_on: checked,
            is_active: false, // valor padrão
            updated_at: new Date().toISOString()
          },
          { onConflict: 'user_id' }
        )
        .select();

      if (error) {
        console.error('[SIDEBAR] ❌ Erro ao salvar estado manual:', error);
        console.error('[SIDEBAR] Detalhes do erro:', JSON.stringify(error, null, 2));
        // Reverter estado local em caso de erro
        setIsPlayerManuallyOn(!checked);
        return;
      }

      console.log(`✅ [SIDEBAR] Player ${checked ? 'LIGADO' : 'DESLIGADO'} manualmente - SALVO COM SUCESSO!`);
      console.log('[SIDEBAR] Dados salvos:', data);

      // Verificar se realmente salvou
      const { data: verifyData } = await supabase
        .from('user_shutdown_schedules')
        .select('is_manually_on')
        .eq('user_id', user.id)
        .single();

      console.log('[SIDEBAR] 🔍 Verificação após save:', verifyData);

    } catch (err) {
      console.error('[SIDEBAR] ❌ Erro inesperado ao salvar estado manual:', err);
      setIsPlayerManuallyOn(!checked);
    }
  };

  const handleLogout = async () => {
    try {
      console.log('🔄 DashboardSidebar - Iniciando logout...');
      await signOut();

      // Aguardar um pouco para garantir que o logout seja processado
      setTimeout(() => {
        console.log('🔄 DashboardSidebar - Redirecionando para /auth');
        navigate('/auth');
      }, 500);

    } catch (error) {
      console.error('❌ DashboardSidebar - Erro ao fazer logout:', error);
    }
  };

  const toggleActions = () => {
    setIsActionsCollapsed(!isActionsCollapsed);
  };

  // Função removida: toggleFooter - não é mais necessária

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className={`${onClose ? 'max-h-[85vh] overflow-y-auto pb-4' : 'h-[calc(100vh-80px)] w-80'} flex flex-col bg-gray-50 dark:bg-gray-900`}>
        {/* Header do Sidebar */}
        <div className="p-4 pb-0 flex-shrink-0">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Gerencie suas mídias e dispositivos</p>
              </div>
              {onClose && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Conteúdo Principal - Botões de Ação com scroll interno */}
        <div className={`flex-1 p-4 pt-2 min-h-0 ${onClose ? '' : 'overflow-hidden'}`}>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg p-4 md:p-6 h-full flex flex-col">
            {/* Container scrollável interno com espaçamento adequado */}
            <div className="flex-1 overflow-y-auto sidebar-scroll pr-3 -mr-2">
              {/* Seção AÇÕES */}
              <div className="flex items-center justify-between cursor-pointer py-2" onClick={toggleActions}>
                <h3 className="text-sm font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Watch Station
                </h3>
                <button
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  title={isActionsCollapsed ? "Expandir" : "Recolher"}
                >
                  {isActionsCollapsed ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronUp className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              </div>

              <div className={`transition-all duration-300 overflow-hidden ${isActionsCollapsed ? 'max-h-0 opacity-0' : 'max-h-[500px] opacity-100'}`}>
                <div className={`flex flex-col ${onClose ? 'gap-2' : 'gap-2 md:gap-3'} pb-2`}>
                  <Button
                    variant="outline"
                    className="justify-start h-10 md:h-12 text-left bg-emerald-600 hover:bg-emerald-700 border-emerald-600 hover:border-emerald-700 text-white transition-all duration-200"
                    onClick={() => {
                      onNewScreen();
                      onClose?.();
                    }}
                  >
                    <Monitor className="mr-2 md:mr-3 h-4 md:h-5 w-4 md:w-5 text-white" />
                    <span className="font-medium text-white text-sm md:text-base">Novo Grupo</span>
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start h-10 md:h-12 text-left bg-teal-600 hover:bg-teal-700 border-teal-600 hover:border-teal-700 text-white transition-all duration-200"
                    onClick={() => {
                      onNewMedia();
                      onClose?.();
                    }}
                  >
                    <Upload className="mr-2 md:mr-3 h-4 md:h-5 w-4 md:w-5 text-white" />
                    <span className="font-medium text-white text-sm md:text-base">Nova Mídia</span>
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start h-10 md:h-12 text-left bg-green-600 hover:bg-green-700 border-green-600 hover:border-green-700 text-white transition-all duration-200"
                    onClick={() => {
                      onNewDevice();
                      onClose?.();
                    }}
                  >
                    <Smartphone className="mr-2 md:mr-3 h-4 md:h-5 w-4 md:w-5 text-white" />
                    <span className="font-medium text-white text-sm md:text-base">Novo Aparelho</span>
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start h-10 md:h-12 text-left bg-lime-600 hover:bg-lime-700 border-lime-600 hover:border-lime-700 text-white transition-all duration-200"
                    onClick={() => {
                      onNewPromo();
                      onClose?.();
                    }}
                  >
                    <Tag className="mr-2 md:mr-3 h-4 md:h-5 w-4 md:w-5 text-white" />
                    <span className="font-medium text-white text-sm md:text-base">Criar Promoção</span>
                  </Button>
                </div>
              </div>

              {/* Divisória após AÇÕES */}
              <div className="my-3 mx-2 md:mx-4">
                <div className="h-px bg-gray-200 dark:bg-gray-600"></div>
              </div>

              {/* Seção IMPULSIONAR */}
              {settings.show_boost && (
                <>
                  <div className="px-2 md:px-4 py-2 flex items-center justify-between cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors" onClick={() => {
                    if (!hasActivePlan()) {
                      onOpenMyPlan();
                      return;
                    }
                    setIsServicesCollapsed(!isServicesCollapsed);
                  }}>
                    <h3 className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide flex items-center gap-2">
                      <Rocket className="h-3.5 w-3.5" />
                      IMPULSIONAR
                      {!hasActivePlan() && <Lock className="h-3 w-3 ml-1 text-gray-400" />}
                    </h3>
                    <span>
                      {isServicesCollapsed ? (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronUp className="h-4 w-4 text-gray-500" />
                      )}
                    </span>
                  </div>

                  <div className={`transition-all duration-300 overflow-hidden ${isServicesCollapsed ? 'max-h-0 opacity-0' : 'max-h-[200px] opacity-100'}`}>
                    <div className="flex flex-col gap-2 py-2">
                      {settings.show_creatives && (
                        <Button
                          variant="outline"
                          className="justify-start h-10 md:h-12 text-left text-green-600 hover:bg-green-50 hover:text-green-700 border-green-600 hover:border-green-700 transition-all duration-200 w-full"
                          onClick={() => {
                            onNewCreatives();
                            onClose?.();
                          }}
                        >
                          <Palette className="mr-2 md:mr-3 h-4 md:h-5 w-4 md:w-5" />
                          <span className="font-medium text-sm md:text-base">Criativos</span>
                        </Button>
                      )}

                      {settings.show_watch_ads && (
                        <Button
                          variant="outline"
                          className="justify-start h-10 md:h-12 text-left border-0 text-white hover:text-white transition-all duration-200 w-full shadow-md hover:shadow-lg"
                          style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 25%, #22c55e 50%, #16a34a 75%, #10b981 100%)',
                            backgroundSize: '200% 200%',
                            animation: 'gradient-shift 3s ease infinite'
                          }}
                          onClick={() => {
                            onWatchAds();
                            onClose?.();
                          }}
                        >
                          <Tv className="mr-2 md:mr-3 h-4 md:h-5 w-4 md:w-5 text-white" />
                          <span className="font-bold text-sm md:text-base text-white drop-shadow-sm">Watch Ads</span>
                          <span className="ml-auto text-[9px] bg-white/30 px-1.5 py-0.5 rounded-full text-white font-medium">Novo</span>
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Divisória após IMPULSIONAR */}
                  <div className="my-3 mx-2 md:mx-4">
                    <div className="h-px bg-gray-200 dark:bg-gray-600"></div>
                  </div>
                </>
              )}

              {/* Seção SEJA UM PARCEIRO */}
              {settings.show_partner_program && (
                <>
                  <div className="px-2 md:px-4 py-2 flex items-center justify-between cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors" onClick={() => {
                    if (!hasActivePlan()) {
                      onOpenMyPlan();
                      return;
                    }
                    setIsPartnerCollapsed(!isPartnerCollapsed);
                  }}>
                    <h3 className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide flex items-center gap-2">
                      <Handshake className="h-3.5 w-3.5" />
                      Seja um parceiro
                      {!hasActivePlan() && <Lock className="h-3 w-3 ml-1 text-gray-400" />}
                    </h3>
                    <span>
                      {isPartnerCollapsed ? (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronUp className="h-4 w-4 text-gray-500" />
                      )}
                    </span>
                  </div>

                  <div className={`transition-all duration-300 overflow-hidden ${isPartnerCollapsed ? 'max-h-0 opacity-0' : 'max-h-[100px] opacity-100'}`}>
                    {settings.show_venue_ads && (
                      <div className="py-2">
                        <Button
                          variant="outline"
                          className="justify-start h-10 md:h-12 text-left border-0 text-white hover:text-white transition-all duration-200 w-full shadow-md hover:shadow-lg"
                          style={{
                            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 25%, #f97316 75%, #fb923c 100%)',
                            backgroundSize: '200% 200%',
                            animation: 'gradient-shift 3s ease infinite'
                          }}
                          onClick={() => {
                            if (hasActiveDevices) {
                              onOpenVenuePartner();
                              onClose?.();
                            } else {
                              onOpenNoActiveDevices();
                            }
                          }}
                        >
                          <Building2 className="mr-2 md:mr-3 h-4 md:h-5 w-4 md:w-5 text-white" />
                          <span className="font-bold text-sm md:text-base text-white drop-shadow-sm">Venue</span>
                          <span className="ml-auto text-[9px] bg-white/30 px-1.5 py-0.5 rounded-full text-white font-medium">ADS</span>
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Divisória após PARCEIRO */}
                  <div className="my-3 mx-2 md:mx-4">
                    <div className="h-px bg-gray-200 dark:bg-gray-600"></div>
                  </div>
                </>
              )}

              {/* Seção CONFIGURAÇÕES */}
              <div className="px-2 md:px-4 py-2 flex items-center justify-between cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors" onClick={() => setIsConfigCollapsed(!isConfigCollapsed)}>
                <h3 className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide flex items-center gap-2">
                  <Settings className="h-3.5 w-3.5" />
                  Configurações
                </h3>
                <span>
                  {isConfigCollapsed ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronUp className="h-4 w-4 text-gray-500" />
                  )}
                </span>
              </div>

              <div className={`transition-all duration-300 overflow-hidden ${isConfigCollapsed ? 'max-h-0 opacity-0' : 'max-h-[350px] opacity-100'}`}>
                <div className="flex flex-col gap-2 md:gap-3 w-full py-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start h-11 md:h-12 text-left text-green-600 hover:bg-green-50 hover:text-green-700 border-green-600 hover:border-green-700 transition-all duration-200"
                    onClick={() => {
                      onOpenProfile();
                      onClose?.();
                    }}
                  >
                    <User className="mr-2 md:mr-3 h-4 md:h-5 w-4 md:w-5" />
                    <span className="font-medium text-sm md:text-base">Perfil</span>
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full justify-start h-11 md:h-12 text-left text-green-600 hover:bg-green-50 hover:text-green-700 border-green-600 hover:border-green-700 transition-all duration-200"
                    onClick={() => {
                      onChangePassword();
                      onClose?.();
                    }}
                  >
                    <Key className="mr-2 md:mr-3 h-4 md:h-5 w-4 md:w-5" />
                    <span className="font-medium text-sm md:text-base">Alterar senha</span>
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full justify-start h-11 md:h-12 text-left text-green-600 hover:bg-green-50 hover:text-green-700 border-green-600 hover:border-green-700 transition-all duration-200"
                    onClick={() => {
                      onOpenMyPlan();
                      onClose?.();
                    }}
                  >
                    <CreditCard className="mr-2 md:mr-3 h-4 md:h-5 w-4 md:w-5" />
                    <span className="font-medium text-sm md:text-base">Meu Plano</span>
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full justify-start h-11 md:h-12 text-left text-green-600 hover:bg-green-50 hover:text-green-700 border-green-600 hover:border-green-700 transition-all duration-200"
                    onClick={() => {
                      onOpenPaymentHistory();
                      onClose?.();
                    }}
                  >
                    <History className="mr-2 md:mr-3 h-4 md:h-5 w-4 md:w-5" />
                    <span className="font-medium text-sm md:text-base">Histórico</span>
                  </Button>
                </div>
              </div>

              {/* Divisória após CONFIGURAÇÕES */}
              <div className="my-3 mx-2 md:mx-4">
                <div className="h-px bg-gray-200 dark:bg-gray-600"></div>
              </div>

              {/* Seção ON/OFF Player */}
              <div className="px-2 md:px-4 py-2 flex items-center justify-between cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors" onClick={() => {
                if (!hasActivePlan()) {
                  onOpenMyPlan();
                  return;
                }
                setIsShutdownCollapsed(!isShutdownCollapsed);
              }}>
                <h3 className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide flex items-center gap-2">
                  <Power className="h-3.5 w-3.5" />
                  ON/OFF Player
                  {!hasActivePlan() && <Lock className="h-3 w-3 ml-1 text-gray-400" />}
                </h3>
                <span>
                  {isShutdownCollapsed ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronUp className="h-4 w-4 text-gray-500" />
                  )}
                </span>
              </div>

              <div className={`transition-all duration-300 overflow-hidden ${isShutdownCollapsed ? 'max-h-0 opacity-0' : 'max-h-[400px] opacity-100'}`}>
                <div className="px-2 md:px-4 py-3 flex flex-col items-center justify-center space-y-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    Configure os horários de ligar/desligar automaticamente suas telas.
                  </p>
                  <Button
                    onClick={() => {
                      onOpenPlayerSchedule();
                      onClose?.();
                    }}
                    className="w-full max-w-[200px] bg-green-600 hover:bg-green-700 text-white font-medium flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                  >
                    <Clock className="h-4 w-4" />
                    Programar
                  </Button>

                  {/* Divisor */}
                  <div className="w-full flex items-center gap-2 py-1">
                    <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 uppercase">ou manualmente</span>
                    <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
                  </div>

                  {/* Toggle Manual Ligar/Desligar */}
                  <div className={`w-full max-w-[250px] p-3 rounded-lg border transition-all duration-200 ${isPlayerManuallyOn
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-full transition-colors ${isPlayerManuallyOn
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400'
                          : 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400'
                          }`}>
                          <Power className="h-3.5 w-3.5" />
                        </div>
                        <div>
                          <span className={`text-xs font-medium ${isPlayerManuallyOn
                            ? 'text-green-700 dark:text-green-300'
                            : 'text-red-700 dark:text-red-300'
                            }`}>
                            {isPlayerManuallyOn ? 'Ligado' : 'Desligado'}
                          </span>
                          <p className="text-[10px] text-gray-500 dark:text-gray-400">
                            {isPlayerManuallyOn ? 'Player ativo' : 'Tela apagada'}
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={isPlayerManuallyOn}
                        onCheckedChange={handleManualToggle}
                        disabled={isLoadingManualToggle}
                        className={isLoadingManualToggle ? 'opacity-50' : ''}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Painel do Usuário - FORA do scroll, sempre visível */}
            <div className="flex-shrink-0 pt-3 mt-2 border-t border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2 md:gap-3 p-2 md:p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs md:text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user?.email || 'Usuário'}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-all duration-200 group"
                  title="Sair da conta"
                >
                  <LogOut className="h-4 w-4 text-gray-400 group-hover:text-red-500 transition-colors" />
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default DashboardSidebar;
